import { useEffect, useState, useMemo } from "react";
import { api, idr } from "../lib/api";
import {
  Plus, Trash2, X, CheckCircle2, XCircle, Pencil, Send, Clock, User, Smartphone,
  AlertTriangle, FileText, Eye, CreditCard, Split, Repeat, Phone, MapPin, StickyNote,
  Sparkles, Lock
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { bolehPakaiAI } from "../lib/izinAI";
import BarcodeScanner from "../components/hris/BarcodeScanner";
import AIProductChat from "../components/hris/AIProductChat";
import InputRupiah from "../components/InputRupiah";
import { useCabangOptions } from "../lib/cabang";
import { DatePicker } from "@/components/ui/date-picker";
import { hariIniISO } from "@/lib/tanggal";
import {
  PaymentMethodSelect, MetodePembayaranManager, TombolKelolaMetode, bolehKelolaMetode,
} from "../components/pembayaran/MetodePembayaran";
import { useModalGuard, UnsavedGuardBar } from "../hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

const QC_STATUSES = ["Pending", "Passed", "Failed"];

const emptyForm = () => ({
  id: null,
  tanggal: hariIniISO(),
  waktu: new Date().toTimeString().slice(0, 5),
  nama_pelanggan: "", no_hp: "",
  no_rekening: "", nama_bank: "",
  type_hp: "",
  // Kondisi unit second (brief owner 29 Agu poin 3-5). Wajib diisi untuk
  // akuisisi baru — backend menegakkannya juga.
  status_unit: "", warna: "", kelengkapan: "",
  imei: "",
  harga_akuisisi: 0,
  qc_check: "Pending", qc_notes: "",
  catatan: "", nama_penerima: "", branch: "",
  kode: "HP",
  metode_pembayaran: "Cash",
  is_split: false,
  payments: [{ method: "Cash", amount: 0 }],
  trade_in: false, trade_in_note: "",
  trade_in_items: [{ nama: "", harga: 0, imei: "" }],
});

const STATUS_META = {
  pending:  { label: "Menunggu Approval", color: "amber",   icon: Clock },
  approved: { label: "Disetujui",         color: "emerald", icon: CheckCircle2 },
  rejected: { label: "Ditolak",           color: "rose",    icon: XCircle },
};

export default function InputNotaAkuisisi() {
  const { user, can } = useAuth();
  const BRANCHES = useCabangOptions({ denganKosong: true });
  const canApprove = ["superadmin", "admin", "kasir_pos"].includes(user?.role);
  // Cermin `payment_methods_module._boleh_kelola` di backend — lihat InputNota.jsx.
  const canManageMethods = bolehKelolaMetode(user);

  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  // Filter tanggal (brief owner 29 Agu, Akuisisi poin 2). Dikirim ke SERVER —
  // daftarnya dipotong di server, jadi menyaring di layar akan melewatkan nota
  // yang tidak pernah terkirim dan melapor "tidak ada" dengan yakin.
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const [showMine, setShowMine] = useState(!canApprove);
  const [summary, setSummary] = useState({ pending: 0, approved: 0, rejected: 0 });
  // Pilihan kondisi unit (brief owner 29 Agu poin 3 & 5) — dari SERVER supaya
  // owner bisa mengkonfigurasinya tanpa deploy ulang.
  const [opsiKondisi, setOpsiKondisi] = useState({ status_unit: [], kelengkapan: [] });
  useEffect(() => {
    api.get("/acquisitions/opsi-kondisi")
      .then(r => setOpsiKondisi(r.data || {}))
      .catch(() => { /* dropdown kosong; backend tetap menolak nilai kosong */ });
  }, []);

  const [opsiModal, setOpsiModal] = useState(false);
  const [modal, setModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [aiPanel, setAiPanel] = useState(false); // panel chat AI (hanya relevan saat modal terbuka)
  // PIN persetujuan hapus (brief #23) — retry() menyimpan aksi delete yang
  // sempat dibalas 428, dipanggil ulang begitu PIN dikirim.
  const [pinDialog, setPinDialog] = useState(null); // {pin, submitting, error, retry}

  // Employees + payment methods
  const [employees, setEmployees] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentGroups, setPaymentGroups] = useState({});
  const [paymentGroupsAll, setPaymentGroupsAll] = useState({});
  const [methodsModal, setMethodsModal] = useState(false);

  // "mine" vs "all" scope for Nama Penerima
  const [penerimaScope, setPenerimaScope] = useState("mine");

  // Pengurutan tabel daftar nota (client-side, lihat components/ui/sort.jsx) —
  // urutan bawaan tetap seperti sebelumnya (backend sudah `.sort("created_at",
  // -1).limit(500)`, jadi sort di sini HANYA menata ulang 500 nota terbaru
  // yang sudah termuat, bukan seluruh data kalau nota lebih dari itu).
  const notaSort = useSortState(null, "asc");

  const load = () => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (showMine) p.set("mine", "true");
    if (dari) p.set("date_from", dari);
    if (sampai) p.set("date_to", sampai);
    api.get(`/nota/acquisitions?${p}`).then(r => setItems(r.data || []));
    api.get("/nota/acquisitions/summary/stats").then(r => setSummary(r.data || {}));
  };

  useEffect(load, [status, showMine, dari, sampai]);

  useEffect(() => {
    api.get("/employees").then(r => setEmployees(Array.isArray(r.data) ? r.data : (r.data?.items || [])));
    loadMethods();
  }, []);

  const loadMethods = () => {
    api.get("/payment-methods?active_only=true").then(r => {
      setPaymentMethods(r.data?.items || []);
      setPaymentGroups(r.data?.groups || {});
    });
    api.get("/payment-methods?active_only=false").then(r => {
      setPaymentGroupsAll(r.data?.groups || {});
    });
  };

  const totalSplit = useMemo(
    () => (form.payments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0),
    [form.payments]
  );
  // Nama unit second sebagai SATU kesatuan (poin 6). Aturannya SAMA dengan
  // `rakit_nama_unit_akuisisi` di backend; yang tersimpan tetap hasil hitungan
  // SERVER — ini cuma pratinjau supaya kasir tahu apa yang akan tercatat.
  const namaUnitGabungan = useMemo(
    () => [form.type_hp, form.storage, form.status_unit, form.warna, form.kelengkapan]
      .map(x => (x || "").trim()).filter(Boolean).join(" / "),
    [form.type_hp, form.storage, form.status_unit, form.warna, form.kelengkapan],
  );

  const tradeInTotal = useMemo(
    () => form.trade_in ? (form.trade_in_items || []).reduce((s, t) => s + (parseFloat(t.harga) || 0), 0) : 0,
    [form.trade_in, form.trade_in_items]
  );
  // harga_akuisisi = totalPayments + tradeInTotal → VALID
  const splitBalance = (parseFloat(form.harga_akuisisi) || 0) - totalSplit - tradeInTotal;

  // Live clock — auto-update waktu every 30s while create modal open (mirrors Sales)
  useEffect(() => {
    if (!modal || modalMode !== "create") return;
    const tick = () => setForm(f => ({ ...f, waktu: new Date().toTimeString().slice(0, 5) }));
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [modal, modalMode]);

  const availableEmployees = useMemo(() => {
    if (penerimaScope === "mine") {
      const byEmail = employees.filter(e => user?.email && e.email === user.email);
      if (byEmail.length) return byEmail;
      return employees.filter(e => e.nama === user?.name);
    }
    return employees;
  }, [penerimaScope, employees, user]);

  const openCreate = () => {
    const me = employees.find(e => e.email === user?.email) || employees.find(e => e.nama === user?.name);
    setForm({
      ...emptyForm(),
      nama_penerima: me?.nama || user?.name || "",
      branch: me?.branch || "",
    });
    setPenerimaScope("mine");
    setModalMode("create");
    setModal(true);
  };

  const openEdit = (n) => {
    setForm({
      id: n.id,
      tanggal: n.tanggal, waktu: n.waktu,
      nama_pelanggan: n.nama_pelanggan || "", no_hp: n.no_hp || "",
      no_rekening: n.no_rekening || "", nama_bank: n.nama_bank || "",
      type_hp: n.type_hp || "",
      status_unit: n.status_unit || "", warna: n.warna || "",
      kelengkapan: n.kelengkapan || "",
      imei: n.imei || "",
      harga_akuisisi: n.harga_akuisisi || 0,
      qc_check: n.qc_check || "Pending", qc_notes: n.qc_notes || "",
      catatan: n.catatan || "", nama_penerima: n.nama_penerima || "", branch: n.branch || "",
      kode: n.kode || "HP",
      metode_pembayaran: n.metode_pembayaran || "Cash",
      is_split: !!(n.payments && n.payments.length > 0) || !!n.is_split,
      payments: n.payments?.length ? n.payments : [{ method: n.metode_pembayaran || "Cash", amount: 0 }],
      trade_in: !!n.trade_in, trade_in_note: n.trade_in_note || "",
      trade_in_items: n.trade_in_items?.length
        ? n.trade_in_items
        : (n.trade_in && n.trade_in_note ? [{ nama: n.trade_in_note, harga: 0 }] : [{ nama: "", harga: 0 }]),
    });
    const inMine = employees.some(e => (e.nama === user?.name || e.email === user?.email) && e.nama === n.nama_penerima);
    setPenerimaScope(inMine ? "mine" : "all");
    setModalMode("edit");
    setDetail(null);
    setModal(true);
  };

  const submit = async () => {
    // Kolom wajib brief owner 29 Agu (Akuisisi poin 2). Daftarnya SAMA persis
    // dengan `_validasi_kolom_wajib_akuisisi` di backend — dicek di sini hanya
    // supaya pesannya menyebut kolom yang kosong satu per satu, bukan sebagai
    // pengganti penjagaan server.
    const wajib = [
      ["Nama pelanggan", form.nama_pelanggan], ["No. HP", form.no_hp],
      ["No. Rekening", form.no_rekening], ["Nama Bank", form.nama_bank],
      ["Type barang", form.type_hp], ["Status", form.status_unit],
      ["Warna", form.warna], ["IMEI/SN", form.imei],
      ["Kelengkapan", form.kelengkapan],
    ];
    const kurang = wajib.filter(([, v]) => !String(v || "").trim()).map(([k]) => k);
    if (kurang.length) { toast.error("Wajib diisi: " + kurang.join(", ")); return; }
    if (!form.nama_penerima.trim()) { toast.error("Nama penerima wajib diisi"); return; }
    if (!form.harga_akuisisi || form.harga_akuisisi <= 0) { toast.error("Harga akuisisi wajib diisi"); return; }
    if (form.is_split || form.trade_in) {
      if (Math.abs(splitBalance) > 0.5) {
        const parts = [];
        if (form.is_split) parts.push(`pembayaran ${idr(totalSplit)}`);
        if (form.trade_in) parts.push(`trade-in ${idr(tradeInTotal)}`);
        if (!confirm(`Harga akuisisi ${idr(form.harga_akuisisi)} ≠ ${parts.join(" + ")}. Selisih ${idr(splitBalance)}. Lanjutkan simpan?`)) return;
      }
    }
    if (form.trade_in) {
      const validTradeItems = (form.trade_in_items || []).filter(t => t.nama.trim());
      if (validTradeItems.length === 0) { toast.error("Isi minimal 1 unit trade-in dengan nama"); return; }
    }
    const payload = { ...form };
    if (!form.is_split) payload.payments = null;
    if (!form.trade_in) {
      payload.trade_in_items = null;
      payload.trade_in_note = "";
    } else {
      payload.trade_in_items = (form.trade_in_items || [])
        .filter(t => t.nama.trim())
        .map(t => ({ ...t, imei: (t.imei || "").trim() }));
      payload.trade_in_note = payload.trade_in_items
        .map(t => `${t.nama}${t.imei ? ` [IMEI ${t.imei}]` : ""}${t.harga ? ` (${idr(t.harga)})` : ""}`)
        .join(", ");
    }
    setSaving(true);
    try {
      if (modalMode === "create") {
        await api.post("/nota/acquisitions", payload);
        toast.success("Nota akuisisi terkirim — menunggu approval");
      } else {
        await api.patch(`/nota/acquisitions/${form.id}`, payload);
        toast.success("Nota diperbarui");
      }
      setModal(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };

  const approve = async (n) => {
    try {
      const { data } = await api.post(`/nota/acquisitions/${n.id}/approve`);
      toast.success(`✓ Nota akuisisi disetujui & ditransfer (ID: ${data.acquisition_id?.slice(0, 8)}...)`);
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal approve akuisisi");
    }
  };

  const openReject = (n) => { setRejectModal(n); setRejectReason(""); };
  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error("Alasan wajib diisi"); return; }
    try {
      await api.post(`/nota/acquisitions/${rejectModal.id}/reject`, { reason: rejectReason.trim() });
      toast.success("Nota ditolak");
      setRejectModal(null); setDetail(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal reject"); }
  };

  const doDeleteNota = async (id, pin) => {
    await api.delete(`/nota/acquisitions/${id}`, pin ? { params: { pin } } : undefined);
    toast.success("Nota dihapus");
    setDetail(null);
    load();
  };

  // Brief #23 — DELETE bisa membalas 428 (butuh PIN persetujuan pemegang izin
  // Pusat Approval). Ajukan otomatis, lalu buka dialog PIN.
  const deleteNota = async (n) => {
    if (!confirm(`Hapus nota ini?`)) return;
    try {
      await doDeleteNota(n.id);
    } catch (e) {
      if (e?.response?.status === 428) {
        try {
          await api.post("/aktivitas/hapus/ajukan", {
            modul: "nota_akuisisi", target_id: n.id,
            ringkasan: `Hapus nota akuisisi ${n.type_hp || ""}`.trim(),
          });
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Gagal mengajukan izin hapus");
          return;
        }
        setPinDialog({ pin: "", submitting: false, error: "", retry: (pin) => doDeleteNota(n.id, pin) });
      } else {
        toast.error(e?.response?.data?.detail || "Gagal hapus");
      }
    }
  };

  const submitPinDialog = async () => {
    if (!pinDialog) return;
    setPinDialog(d => ({ ...d, submitting: true, error: "" }));
    try {
      await pinDialog.retry(pinDialog.pin);
      setPinDialog(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setPinDialog(d => ({ ...d, submitting: false, error: "PIN salah atau sudah kedaluwarsa — coba lagi." }));
      } else {
        toast.error(e?.response?.data?.detail || "Gagal menghapus");
        setPinDialog(d => ({ ...d, submitting: false }));
      }
    }
  };

  // Terima hasil urai AI. Form akuisisi memuat SATU unit per nota (type_hp/
  // harga/imei adalah field tunggal), jadi hanya item pertama yang diisikan;
  // sisanya diberi tahu lewat toast supaya kasir membuat nota terpisah —
  // bukan diam-diam dibuang tanpa dia sadar.
  const applyAiItems = (rows) => {
    if (!rows?.length) return;
    const r = rows[0];
    setForm(f => ({
      ...f,
      type_hp: r.type_hp || f.type_hp,
      harga_akuisisi: parseFloat(r.harga_akuisisi) || f.harga_akuisisi,
      imei: (r.imei || "").trim() || f.imei,
    }));
    if (rows.length > 1) {
      toast.info(`Form akuisisi hanya memuat 1 unit — "${r.type_hp}" dimasukkan, ${rows.length - 1} unit lain perlu nota terpisah.`);
    } else {
      toast.success("Data unit hasil AI dimasukkan ke form");
    }
  };

  // Payment helpers
  const addPay = () => setForm(f => ({ ...f, payments: [...(f.payments || []), { method: paymentMethods[0]?.name || "Cash", amount: 0 }] }));
  const rmPay = (i) => setForm(f => ({ ...f, payments: (f.payments || []).filter((_, j) => j !== i) }));
  const updPay = (i, k, v) => setForm(f => {
    const arr = [...(f.payments || [])];
    arr[i] = { ...arr[i], [k]: k === "amount" ? (parseFloat(v) || 0) : v };
    return { ...f, payments: arr };
  });

  // Trade-in item helpers (multi-unit)
  const addTradeItem = () => setForm(f => ({ ...f, trade_in_items: [...(f.trade_in_items || []), { nama: "", harga: 0, imei: "" }] }));
  const rmTradeItem = (i) => setForm(f => ({ ...f, trade_in_items: (f.trade_in_items || []).filter((_, j) => j !== i) }));
  const updTradeItem = (i, k, v) => setForm(f => {
    const arr = [...(f.trade_in_items || [])];
    arr[i] = { ...arr[i], [k]: k === "harga" ? (parseFloat(v) || 0) : v };
    return { ...f, trade_in_items: arr };
  });
  const autoFillTradeInBalance = () => {
    const gap = (parseFloat(form.harga_akuisisi) || 0) - totalSplit;
    if (gap <= 0) { toast.info("Tidak ada selisih untuk di-cover trade-in"); return; }
    const items = form.trade_in_items || [];
    if (items.length === 0) {
      setForm(f => ({ ...f, trade_in_items: [{ nama: "", harga: gap, imei: "" }] }));
    } else {
      const others = items.slice(0, -1);
      const otherSum = others.reduce((s, t) => s + (parseFloat(t.harga) || 0), 0);
      const arr = [...items];
      arr[arr.length - 1] = { ...arr[arr.length - 1], harga: Math.max(0, gap - otherSum) };
      setForm(f => ({ ...f, trade_in_items: arr }));
    }
    toast.success(`Trade-in di-set jadi ${idr(gap)}`);
  };

  // Pengaman isian belum tersimpan (brief owner) — form Buat/Edit Nota
  // Akuisisi adalah form terpanjang di halaman ini (unit + trade-in + IMEI).
  // Hook dipanggil di level komponen (bukan di dalam JSX kondisional
  // `{modal && ...}`), jadi `active` WAJIB diikat ke flag buka/tutup modalnya
  // sendiri supaya status "dirty" & konfirmasi ikut reset tiap modal dibuka
  // ulang (lihat docstring `active` di hooks/useModalGuard.jsx).
  const notaGuard = useModalGuard({
    onClose: () => setModal(false), onSubmit: submit, busy: saving, dangerous: true, active: modal,
  });

  return (
    <div className="p-4 md:p-8 max-w-[1400px]" data-testid="input-nota-akuisisi-page">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-violet-700">POS & Sales</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Input Nota Akuisisi Unit</h1>
          <p className="text-xs text-slate-500 mt-1">
            Sales input nota akuisisi HP → Kasir/Admin QC & approve → auto-transfer ke Akuisisi Unit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManageMethods && (
            <TombolKelolaMetode jumlah={paymentMethods.length} testid="btn-manage-methods-akq"
              onClick={() => setMethodsModal(true)} />
          )}
          <button onClick={openCreate} data-testid="btn-new-nota-akuisisi"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium">
            <Plus className="w-4 h-4" /> Buat Nota Akuisisi
          </button>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-5">
        <StatCard I={Clock} label="Menunggu" value={summary.pending || 0} tint="amber" />
        <StatCard I={CheckCircle2} label="Disetujui" value={summary.approved || 0} tint="emerald" />
        <StatCard I={XCircle} label="Ditolak" value={summary.rejected || 0} tint="rose" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex flex-wrap items-center gap-2">
        <label className="text-xs font-medium text-slate-500">Status:</label>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="filter-status-akuisisi"
          className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Semua</option>
          <option value="pending">Menunggu Approval</option>
          <option value="approved">Disetujui</option>
          <option value="rejected">Ditolak</option>
        </select>
        <label className="text-xs font-medium text-slate-500 ml-2">Tanggal:</label>
        <input type="date" value={dari} onChange={e => setDari(e.target.value)}
               data-testid="filter-akq-dari" max={sampai || undefined}
               className="h-9 px-2 rounded-lg border border-slate-200 text-sm" />
        <span className="text-xs text-slate-400">s/d</span>
        <input type="date" value={sampai} onChange={e => setSampai(e.target.value)}
               data-testid="filter-akq-sampai" min={dari || undefined}
               className="h-9 px-2 rounded-lg border border-slate-200 text-sm" />
        {(dari || sampai) && (
          <button onClick={() => { setDari(""); setSampai(""); }} data-testid="filter-akq-reset"
                  className="h-9 px-2.5 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50">
            Reset
          </button>
        )}
        {canApprove && (
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 ml-2">
            <input type="checkbox" checked={showMine} onChange={e => setShowMine(e.target.checked)} className="w-3.5 h-3.5" />
            Hanya nota saya
          </label>
        )}
        {/* Konfigurasi pilihan Status & Kelengkapan (brief poin 3 & 5).
            Hanya untuk yang boleh mengubah akuisisi — endpointnya menolak yang
            lain, jadi tombolnya jangan sampai berujung 403 (aturan 3). */}
        {canApprove && (
          <button onClick={() => setOpsiModal(true)} data-testid="btn-konfig-opsi-akq"
                  className="h-9 px-2.5 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50">
            Atur pilihan Status/Kelengkapan
          </button>
        )}
        <span className="ml-auto text-[11px] text-slate-500">{items.length} nota</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} testid="nota-akq-sort-tanggal" />
              <SortTh label="Pelanggan" sortKeyName="nama_pelanggan" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} testid="nota-akq-sort-pelanggan" />
              <SortTh label="Type HP" sortKeyName="type_hp" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} testid="nota-akq-sort-typehp" />
              <SortTh label="Penerima" sortKeyName="nama_penerima" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} testid="nota-akq-sort-penerima" />
              <SortTh label="Harga" sortKeyName="harga_akuisisi" sortKey={notaSort.sortKey} sortDir={notaSort.sortDir} onSort={notaSort.toggle} align="right" testid="nota-akq-sort-harga" />
              <th className="text-center py-3 px-4">QC</th>
              <th className="text-center py-3 px-4">Status</th>
              <th className="text-right py-3 px-4">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-sm text-slate-400">
                <Smartphone className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                Belum ada nota akuisisi.
              </td></tr>
            )}
            {sortRows(items, notaSort.sortKey, notaSort.sortDir).map(n => {
              const meta = STATUS_META[n.status] || STATUS_META.pending;
              const canEdit = n.status !== "approved" && (n.created_by_id === user?.id || canApprove);
              return (
                <tr key={n.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`nota-akq-row-${n.id}`}>
                  <td className="py-3 px-4">
                    <p className="text-xs text-slate-900 font-medium">{n.tanggal}</p>
                    <p className="text-[10px] text-slate-500">{n.waktu}</p>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-sm font-medium text-slate-900">{n.nama_pelanggan}</p>
                    <p className="text-[10px] text-slate-500">{n.no_hp || "-"}</p>
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-700 font-medium">
                    {n.type_hp}
                    {n.trade_in && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-violet-100 text-violet-700 font-bold">TRADE-IN</span>}
                    {n.imei && <span className="block font-mono text-[10px] text-slate-400 mt-0.5">{n.imei}</span>}
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-600">{n.nama_penerima}</td>
                  <td className="py-3 px-4 text-right font-mono font-semibold text-slate-900">{idr(n.harga_akuisisi)}</td>
                  <td className="py-3 px-4 text-center">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                      n.qc_check === "Passed" ? "bg-emerald-100 text-emerald-800" :
                      n.qc_check === "Failed" ? "bg-rose-100 text-rose-800" :
                      "bg-amber-100 text-amber-800"
                    }`}>{n.qc_check}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-${meta.color}-100 text-${meta.color}-800`}
                      data-testid={`nota-akq-status-${n.id}`}>
                      <meta.icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button onClick={() => setDetail(n)} title="Detail"
                      className="p-2 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700">
                      <Eye className="w-4 h-4" />
                    </button>
                    {canEdit && (
                      <button onClick={() => openEdit(n)} title="Edit"
                        className="p-2 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-700">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {canApprove && n.status === "pending" && (
                      <>
                        <button onClick={() => approve(n)} title="Setujui" data-testid={`btn-approve-akq-${n.id}`}
                          className="p-2 rounded hover:bg-emerald-50 text-slate-500 hover:text-emerald-700">
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => openReject(n)} title="Tolak" data-testid={`btn-reject-akq-${n.id}`}
                          className="p-2 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-700">
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {(n.status !== "approved") && (n.created_by_id === user?.id || user?.role === "admin" || user?.role === "superadmin") && (
                      <button onClick={() => deleteNota(n)} title="Hapus"
                        className="p-2 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal: Create / Edit */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={notaGuard.requestClose}>
          <div ref={notaGuard.contentRef} className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="nota-akuisisi-modal">
            <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h3 className="font-display font-semibold text-base">
                  {modalMode === "create" ? "Buat Nota Akuisisi" : "Edit Nota Akuisisi"}
                </h3>
                <p className="text-[11px] text-violet-100">Akan diteruskan ke kasir untuk QC & approve.</p>
              </div>
              <div className="flex items-center gap-2">
                {/* Gerbang fitur AI (brief owner 30 Agu 2026) — kunci
                    `ai_assistant`, sama dengan backend /ai-produk/parse. */}
                {bolehPakaiAI(user, can) && (
                <button onClick={() => setAiPanel(true)} data-testid="btn-input-via-ai-akq"
                  title="Tempel teks bebas — AI mengisi type HP, harga, dan IMEI"
                  className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 font-medium">
                  <Sparkles className="w-3.5 h-3.5" /> Input via AI
                </button>
                )}
                <button onClick={notaGuard.requestClose} className="p-2 hover:bg-white/20 rounded-lg">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-3 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Tanggal">
                  <DatePicker value={form.tanggal} onChange={v => setForm(f => ({ ...f, tanggal: v }))} />
                </Field>
                <Field label={<span className="inline-flex items-center gap-1">Waktu <span className="text-[9px] px-1.5 py-0 rounded bg-violet-100 text-violet-800 font-bold">LIVE</span></span>}>
                  <input type="time" value={form.waktu} readOnly disabled
                    data-testid="input-waktu-akq"
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-slate-50 text-slate-600 cursor-not-allowed" />
                </Field>
                <Field label="Cabang">
                  <select value={form.branch} onChange={e => setForm(f => ({ ...f, branch: e.target.value }))}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    {BRANCHES.map(b => <option key={b} value={b}>{b || "— pilih —"}</option>)}
                  </select>
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Nama Pelanggan *">
                  <input value={form.nama_pelanggan} onChange={e => setForm(f => ({ ...f, nama_pelanggan: e.target.value }))}
                    data-testid="input-akq-pelanggan"
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                </Field>
                <Field label="No HP">
                  <input value={form.no_hp} onChange={e => setForm(f => ({ ...f, no_hp: e.target.value }))}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                </Field>
                <Field label="No Rekening">
                  <input value={form.no_rekening} onChange={e => setForm(f => ({ ...f, no_rekening: e.target.value }))}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                </Field>
                <Field label="Nama Bank">
                  <input value={form.nama_bank} onChange={e => setForm(f => ({ ...f, nama_bank: e.target.value }))}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Type HP / Barang *">
                  <input value={form.type_hp} onChange={e => setForm(f => ({ ...f, type_hp: e.target.value }))}
                    placeholder="mis. iPhone 13 128GB" data-testid="input-type-hp"
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                </Field>
                {/* Status & Warna diletakkan SETELAH type (brief poin 3-4). */}
                <Field label="Status *">
                  <select value={form.status_unit || ""} data-testid="input-akq-status"
                    onChange={e => setForm(f => ({ ...f, status_unit: e.target.value }))}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="">— pilih status —</option>
                    {(opsiKondisi.status_unit || []).map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </Field>
                <Field label="Warna *">
                  <input value={form.warna || ""} data-testid="input-akq-warna"
                    onChange={e => setForm(f => ({ ...f, warna: e.target.value }))}
                    placeholder="mis. Starlight"
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
                </Field>
                <Field label="IMEI / Serial Number *">
                  <div className="flex gap-2">
                    <input value={form.imei} onChange={e => setForm(f => ({ ...f, imei: e.target.value }))}
                      placeholder="IMEI / SN" data-testid="input-imei-akq"
                      inputMode="numeric" maxLength={30}
                      className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-sm font-mono" />
                    <BarcodeScanner testId="btn-scan-imei-akq" title="Scan IMEI unit akuisisi"
                      onDetected={val => setForm(f => ({ ...f, imei: val }))} />
                  </div>
                </Field>
                {/* Kelengkapan SETELAH IMEI/SN (brief poin 5). */}
                <Field label="Kelengkapan *">
                  <select value={form.kelengkapan || ""} data-testid="input-akq-kelengkapan"
                    onChange={e => setForm(f => ({ ...f, kelengkapan: e.target.value }))}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="">— pilih kelengkapan —</option>
                    {(opsiKondisi.kelengkapan || []).map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </Field>
                {/* Output tergabung diperlihatkan SEBELUM disimpan (poin 6) —
                    kasir melihat nama yang akan benar-benar tercatat, bukan
                    menebaknya dari lima kolom terpisah. IMEI sengaja di BAWAH
                    nama, bukan di dalamnya. */}
                {namaUnitGabungan && (
                  <div className="md:col-span-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2"
                       data-testid="akq-nama-gabungan">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wide">Tersimpan sebagai</p>
                    <p className="text-sm font-mono text-slate-900">{namaUnitGabungan}</p>
                    {form.imei && <p className="text-[11px] font-mono text-slate-500">IMEI/SN: {form.imei}</p>}
                  </div>
                )}
                <Field label="Harga Akuisisi *">
                  <InputRupiah value={form.harga_akuisisi} onChange={n => setForm(f => ({ ...f, harga_akuisisi: n }))}
                    testid="input-harga-akq"
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm text-right font-mono" />
                </Field>
                <Field label={
                  <div className="flex items-center justify-between">
                    <span>Nama Penerima *</span>
                    <label className="inline-flex items-center gap-1 text-[10px] text-slate-500 font-normal cursor-pointer">
                      <input type="checkbox" checked={penerimaScope === "all"} onChange={e => setPenerimaScope(e.target.checked ? "all" : "mine")}
                        data-testid="penerima-scope-all" className="w-3 h-3" />
                      Pilih dari semua karyawan
                    </label>
                  </div>
                }>
                  <select value={form.nama_penerima} onChange={e => setForm(f => ({ ...f, nama_penerima: e.target.value }))}
                    data-testid="input-penerima"
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="">— pilih karyawan —</option>
                    {availableEmployees.map(e => (
                      <option key={e.id} value={e.nama}>
                        {e.nama}{e.branch ? ` · ${e.branch}` : ""}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="QC Check">
                  <select value={form.qc_check} onChange={e => setForm(f => ({ ...f, qc_check: e.target.value }))}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    {QC_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="QC Notes">
                <textarea value={form.qc_notes} onChange={e => setForm(f => ({ ...f, qc_notes: e.target.value }))}
                  rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </Field>

              {/* Payment section */}
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5" /> Metode Pembayaran (ke Pelanggan)
                  </p>
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={form.is_split}
                      onChange={e => setForm(s => ({
                        ...s,
                        is_split: e.target.checked,
                        payments: e.target.checked && (!s.payments || s.payments.length === 0)
                          ? [{ method: s.metode_pembayaran || "Cash", amount: s.harga_akuisisi || 0 }]
                          : s.payments,
                      }))}
                      data-testid="nota-akq-split-toggle" />
                    <Split className="w-3.5 h-3.5" /> Split Payment
                  </label>
                </div>

                {!form.is_split ? (
                  <PaymentMethodSelect
                    value={form.metode_pembayaran}
                    onChange={v => setForm(f => ({ ...f, metode_pembayaran: v }))}
                    groups={paymentGroups}
                    testid="nota-akq-metode"
                  />
                ) : (
                  <div className="space-y-2">
                    {(form.payments || []).map((p, i) => (
                      <div key={i} className="grid grid-cols-[1fr,140px,auto] sm:grid-cols-[1fr,180px,auto] gap-2 items-center">
                        <PaymentMethodSelect
                          value={p.method}
                          onChange={v => updPay(i, "method", v)}
                          groups={paymentGroups}
                          testid={`nota-akq-pay-${i}-method`}
                        />
                        <InputRupiah value={p.amount || ""}
                          onChange={n => updPay(i, "amount", n)}
                          testid={`nota-akq-pay-${i}-amount`}
                          className="h-9 px-3 rounded border border-slate-200 text-sm text-right font-mono" />
                        {(form.payments || []).length > 1 && (
                          <button onClick={() => rmPay(i)}
                            className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-1">
                      <button onClick={addPay} data-testid="btn-add-pay-akq"
                        className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:text-violet-800">
                        <Plus className="w-3.5 h-3.5" /> Tambah Metode
                      </button>
                      <div className="text-xs text-right">
                        <p className="text-slate-500">Total split: <span className="font-mono font-semibold text-slate-900">{idr(totalSplit)}</span></p>
                        <p className={Math.abs(splitBalance) < 0.5 ? "text-emerald-700" : "text-amber-700"}>
                          {Math.abs(splitBalance) < 0.5 ? "✓ Sesuai harga akuisisi" : `Selisih ${idr(splitBalance)}`}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Trade-in DIHILANGKAN dari nota akuisisi (brief owner 29 Agu
                  poin 7). Akuisisi adalah PEMBELIAN unit dari orang — tukar
                  tambah adalah peristiwa PENJUALAN dan tempatnya di Input Nota
                  Sales. Dua pintu untuk satu peristiwa membuat unit yang sama
                  bisa tercatat dua kali.

                  Medannya TIDAK dihapus dari model maupun dokumen: akuisisi
                  lama yang terlanjur menandainya harus tetap terbaca apa adanya
                  (aturan 5 CLAUDE.md), dan daftar di atas masih menampilkan
                  label TRADE-IN untuk dokumen-dokumen itu. */}

              {form.trade_in && (
                <div className="border border-violet-200 rounded-lg p-3 bg-violet-50/40">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-violet-700 inline-flex items-center gap-1.5">
                      <Repeat className="w-3.5 h-3.5" /> Unit Trade-in
                    </p>
                    <div className="flex items-center gap-2">
                      <button onClick={autoFillTradeInBalance} type="button" data-testid="btn-tradein-autofill-akq"
                        title="Set trade-in = selisih Harga Akuisisi − Pembayaran"
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white font-medium">
                        Auto-Fill Selisih
                      </button>
                      <button onClick={addTradeItem} type="button" data-testid="btn-add-tradein-akq"
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-violet-100 text-violet-800 hover:bg-violet-200 font-medium">
                        <Plus className="w-3 h-3" /> Tambah Unit
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {(form.trade_in_items || []).map((t, i) => (
                      <div key={i} className="grid grid-cols-[1fr,170px,140px,auto] gap-2 items-center">
                        <input value={t.nama}
                          onChange={e => updTradeItem(i, "nama", e.target.value)}
                          placeholder="Nama unit trade-in (mis. iPhone 15 128GB)"
                          data-testid={`tradein-name-akq-${i}`}
                          className="h-9 px-2 rounded border border-slate-200 text-xs bg-white" />
                        <input value={t.imei || ""}
                          onChange={e => updTradeItem(i, "imei", e.target.value)}
                          placeholder="IMEI / SN (opsional)"
                          inputMode="numeric" maxLength={30}
                          data-testid={`tradein-imei-akq-${i}`}
                          className="h-9 px-2 rounded border border-slate-200 text-xs font-mono bg-white" />
                        <InputRupiah value={t.harga || ""}
                          onChange={n => updTradeItem(i, "harga", n)}
                          placeholder="Nilai Rp"
                          testid={`tradein-price-akq-${i}`}
                          className="h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono bg-white" />
                        <button onClick={() => rmTradeItem(i)} type="button"
                          disabled={(form.trade_in_items || []).length <= 1}
                          className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600 disabled:opacity-30">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-right text-xs text-violet-700 mt-2">
                    Total Trade-in: <span className="font-mono font-bold text-base">{idr(tradeInTotal)}</span>
                  </p>
                </div>
              )}

              {(form.is_split || form.trade_in) && (
                <div className={`text-xs rounded-lg p-2.5 border ${Math.abs(splitBalance) < 0.5 ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-amber-50 border-amber-300 text-amber-800"}`}
                  data-testid="balance-indicator-akq">
                  <p className="font-semibold flex items-center gap-1.5">
                    {Math.abs(splitBalance) < 0.5 ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    {Math.abs(splitBalance) < 0.5
                      ? "✓ Nota Valid: Harga Akuisisi = Pembayaran + Trade-in"
                      : `Selisih ${idr(splitBalance)} — Harga Akuisisi ${idr(form.harga_akuisisi)} ≠ Pembayaran ${idr(totalSplit)}${form.trade_in ? ` + Trade-in ${idr(tradeInTotal)}` : ""}`}
                  </p>
                </div>
              )}

              <Field label="Catatan">
                <textarea value={form.catatan} onChange={e => setForm(f => ({ ...f, catatan: e.target.value }))}
                  rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </Field>
            </div>

            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={notaGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
              <button onClick={submit} disabled={saving} data-testid="btn-save-nota-akuisisi"
                className="h-10 px-5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
                <Send className="w-4 h-4" /> {saving ? "Menyimpan..." : (modalMode === "create" ? "Kirim ke Kasir" : "Simpan")}
              </button>
            </div>
            {notaGuard.confirming && (
              <UnsavedGuardBar onStay={notaGuard.cancelClose} onDiscard={notaGuard.confirmDiscard} testid="nota-akuisisi-guard-bar" />
            )}
          </div>
        </div>
      )}

      {/* Panel chat AI — drawer fixed di atas modal (z-60 > z-50), unmount saat
          modal ditutup sehingga riwayat chat otomatis bersih untuk nota berikutnya */}
      {modal && aiPanel && bolehPakaiAI(user, can) && (
        <AIProductChat mode="akuisisi" onApply={applyAiItems} onClose={() => setAiPanel(false)} />
      )}

      {/* Detail */}
      {detail && (() => {
        const meta = STATUS_META[detail.status] || STATUS_META.pending;
        const canEdit = detail.status !== "approved" && (detail.created_by_id === user?.id || canApprove);
        const totalPayments = (detail.payments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
        return (
          <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
            <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
              data-testid="nota-akq-detail-modal">
              <div className={`bg-gradient-to-r from-${meta.color}-600 to-${meta.color}-500 text-white px-5 py-4 flex items-center justify-between rounded-t-2xl`}>
                <div>
                  <p className="text-[11px] uppercase tracking-wider opacity-90">Detail Nota Akuisisi</p>
                  <h3 className="font-display text-lg font-semibold">{detail.type_hp}</h3>
                  {detail.imei && (
                    <p className="text-[11px] font-mono opacity-90" data-testid="nota-akq-detail-imei">
                      IMEI {detail.imei}
                    </p>
                  )}
                  <p className="text-[11px] opacity-90 inline-flex items-center gap-1"><meta.icon className="w-3 h-3" /> {meta.label}
                    {detail.status === "approved" && detail.approved_ref_id && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-white/20 text-[10px] font-mono">→ Akq #{detail.approved_ref_id.slice(0, 8)}</span>
                    )}
                  </p>
                </div>
                <button onClick={() => setDetail(null)} className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-5 space-y-3 max-h-[75vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <InfoRow icon={User} label="Pelanggan" value={detail.nama_pelanggan} />
                  <InfoRow icon={Phone} label="No HP" value={detail.no_hp || "-"} />
                  <InfoRow icon={FileText} label="Rekening" value={`${detail.nama_bank || "-"} · ${detail.no_rekening || "-"}`} />
                  <InfoRow icon={User} label="Penerima" value={detail.nama_penerima} />
                  <InfoRow icon={Clock} label="Tanggal" value={`${detail.tanggal} · ${detail.waktu}`} />
                  <InfoRow icon={MapPin} label="Cabang" value={detail.branch || "-"} />
                </div>

                <div className="border-t border-slate-100 pt-3 bg-slate-50 rounded p-2">
                  <p className="text-xs font-semibold text-slate-700">Harga Akuisisi: <span className="font-mono text-emerald-700">{idr(detail.harga_akuisisi)}</span></p>
                  <p className="text-xs mt-1">QC: <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${detail.qc_check === "Passed" ? "bg-emerald-100 text-emerald-800" : detail.qc_check === "Failed" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{detail.qc_check}</span></p>
                  {detail.qc_notes && <p className="text-[11px] text-slate-600 mt-1"><b>QC Notes:</b> {detail.qc_notes}</p>}
                </div>

                {/* Payment breakdown */}
                <div className="border-t border-slate-100 pt-3">
                  <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                    <CreditCard className="w-3 h-3" /> Pembayaran ke Pelanggan
                  </p>
                  {detail.payments && detail.payments.length > 0 ? (
                    <div className="space-y-1">
                      {detail.payments.map((p, i) => (
                        <div key={i} className="flex justify-between text-xs bg-sky-50 border border-sky-200 rounded px-2 py-1.5">
                          <span className="text-sky-900 font-medium">{p.method}</span>
                          <span className="font-mono font-semibold text-sky-900">{idr(p.amount)}</span>
                        </div>
                      ))}
                      <p className="text-right text-xs text-slate-500 pt-1">
                        Total: <span className="font-mono font-semibold text-slate-900">{idr(totalPayments)}</span>
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs bg-slate-50 rounded px-2 py-1.5">
                      <b>{detail.metode_pembayaran || "Cash"}</b>
                      <span className="text-slate-500 ml-2">(single method)</span>
                    </p>
                  )}
                </div>

                {/* Trade-in */}
                {detail.trade_in && (
                  <div className="border-t border-slate-100 pt-3">
                    <p className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                      <Repeat className="w-3 h-3" /> Trade-in
                    </p>
                    {detail.trade_in_items && detail.trade_in_items.length > 0 ? (
                      <div className="space-y-1">
                        {detail.trade_in_items.map((t, i) => (
                          <div key={i} className="flex justify-between items-start gap-2 text-xs bg-violet-50 border border-violet-200 rounded px-2 py-1.5">
                            <span className="text-violet-900 font-medium">
                              {t.nama || <span className="italic text-slate-400">tanpa nama</span>}
                              {t.imei && <span className="block font-mono text-[10px] text-violet-600 mt-0.5">IMEI {t.imei}</span>}
                            </span>
                            <span className="font-mono font-semibold text-violet-900 shrink-0">{idr(t.harga)}</span>
                          </div>
                        ))}
                        <p className="text-right text-xs text-slate-500 pt-1">
                          Total trade-in: <span className="font-mono font-semibold text-slate-900">
                            {idr(detail.trade_in_items.reduce((s, t) => s + (parseFloat(t.harga) || 0), 0))}
                          </span>
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs bg-violet-50 border border-violet-200 rounded p-2">
                        <b>Ya</b> — {detail.trade_in_note || <span className="italic text-slate-500">tanpa catatan</span>}
                      </p>
                    )}
                  </div>
                )}

                {detail.catatan && (
                  <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
                    <b className="inline-flex items-center gap-1"><StickyNote className="w-3 h-3" /> Catatan:</b> {detail.catatan}
                  </div>
                )}
                {detail.status === "rejected" && detail.reject_reason && (
                  <div className="text-xs bg-rose-50 border border-rose-200 rounded p-2">
                    <b className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Alasan:</b> {detail.reject_reason}
                  </div>
                )}
                <p className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
                  Dibuat oleh <b>{detail.created_by_name || detail.created_by_email}</b> · {detail.created_at?.slice(0, 16)?.replace("T", " ")}
                </p>
              </div>
              <div className="border-t border-slate-100 p-4 flex flex-wrap justify-end gap-2">
                {canEdit && (
                  <button onClick={() => openEdit(detail)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
                {canApprove && detail.status === "pending" && (
                  <>
                    <button onClick={() => approve(detail)} data-testid="btn-detail-approve-akq"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Setujui & Transfer
                    </button>
                    <button onClick={() => openReject(detail)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium">
                      <XCircle className="w-3.5 h-3.5" /> Tolak
                    </button>
                  </>
                )}
                <button onClick={() => setDetail(null)} className="h-9 px-3 rounded-lg border border-slate-200 text-xs">Tutup</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Reject */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setRejectModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="bg-rose-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm">Tolak Nota Akuisisi</h3>
              <button onClick={() => setRejectModal(null)} className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5">
              <p className="text-xs text-slate-600 mb-3">
                Tolak nota <b>{rejectModal.type_hp}</b> untuk <b>{rejectModal.nama_pelanggan}</b>.
              </p>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                autoFocus rows={3} placeholder="Alasan penolakan..."
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setRejectModal(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
              <button onClick={doReject} className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium">
                Tolak Nota
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment methods manager modal */}
      {opsiModal && (
        <OpsiKondisiModal
          awal={opsiKondisi}
          onClose={() => setOpsiModal(false)}
          onSimpan={hasil => { setOpsiKondisi(hasil); setOpsiModal(false); }}
        />
      )}

      {methodsModal && (
        <MetodePembayaranManager
          groups={paymentGroupsAll}
          warna="violet"
          idSuffix="akq"
          onClose={() => setMethodsModal(false)}
          onChange={loadMethods}
        />
      )}

      {/* PIN PERSETUJUAN HAPUS (brief #23) */}
      {pinDialog && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setPinDialog(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="pin-dialog">
            <div className="bg-rose-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm inline-flex items-center gap-1.5">
                <Lock className="w-4 h-4" /> PIN Persetujuan Hapus
              </h3>
              <button onClick={() => setPinDialog(null)} className="p-1.5 hover:bg-white/20 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-600">
                Penghapusan butuh persetujuan. Minta PIN ke pemegang izin (lihat Pusat Approval), lalu masukkan di sini.
              </p>
              <input value={pinDialog.pin}
                onChange={e => setPinDialog(d => ({ ...d, pin: e.target.value.replace(/\D/g, "").slice(0, 6), error: "" }))}
                maxLength={6} inputMode="numeric" placeholder="6 digit PIN" autoFocus
                data-testid="pin-input"
                className="w-full h-11 px-3 rounded-lg border border-slate-200 text-center text-lg tracking-[0.3em] font-mono" />
              {pinDialog.error && <p className="text-xs text-rose-600">{pinDialog.error}</p>}
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setPinDialog(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Batal
              </button>
              <button onClick={submitPinDialog} disabled={pinDialog.submitting || pinDialog.pin.length !== 6}
                data-testid="pin-kirim"
                className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium">
                {pinDialog.submitting ? "Memproses..." : "Kirim & Hapus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-1.5">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-slate-900 font-medium truncate">{value}</p>
      </div>
    </div>
  );
}

function StatCard({ I, label, value, tint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className={`w-8 h-8 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-2`}>
        <I className="w-4 h-4" />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-display text-2xl font-bold text-slate-900 mt-0.5">{value}</p>
    </div>
  );
}


/** Konfigurasi pilihan Status & Kelengkapan untuk nota akuisisi.
 *
 * Yang disimpan hanya TAMBAHAN. Pilihan bawaan (Resmi/Beacukai/Unlock/Simlock
 * dan lima kelengkapan) ditampilkan sebagai keterangan dan tidak bisa dihapus:
 * nota lama memakainya, dan menghilangkannya membuat nota itu tidak bisa
 * di-edit ulang tanpa mengganti nilainya.
 */
function OpsiKondisiModal({ awal, onClose, onSimpan }) {
  const [status, setStatus] = useState("");
  const [kelengkapan, setKelengkapan] = useState("");
  const [busy, setBusy] = useState(false);
  const [bawaan, setBawaan] = useState({ status_unit: [], kelengkapan: [] });

  useEffect(() => {
    // Kotak diisi ulang dengan tambahan yang SUDAH ada. Kalau dibiarkan kosong,
    // sekali Simpan akan menghapus semua tambahan yang pernah dibuat.
    api.get("/acquisitions/opsi-kondisi")
      .then(r => {
        const d = r.data || {};
        setBawaan(d.bawaan || { status_unit: [], kelengkapan: [] });
        setStatus((d.tambahan?.status_unit || []).join("\n"));
        setKelengkapan((d.tambahan?.kelengkapan || []).join("\n"));
      })
      .catch(() => toast.error("Gagal memuat pilihan yang tersimpan"));
  }, [awal]);

  const simpan = async () => {
    setBusy(true);
    try {
      const pecah = (t) => t.split(/[\n,]/).map(x => x.trim()).filter(Boolean);
      const r = await api.put("/acquisitions/opsi-kondisi", {
        status_unit: pecah(status),
        kelengkapan: pecah(kelengkapan),
      });
      toast.success("Pilihan tersimpan");
      onSimpan({ status_unit: r.data.status_unit || [], kelengkapan: r.data.kelengkapan || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan pilihan");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}
           data-testid="modal-konfig-opsi-akq">
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="font-display font-bold text-base text-slate-900">Atur Pilihan Status &amp; Kelengkapan</p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Satu nilai per baris (boleh juga dipisah koma). Pilihan bawaan selalu ikut tampil.
          </p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600">Status tambahan</label>
            <textarea value={status} onChange={e => setStatus(e.target.value)} rows={3}
                      data-testid="konfig-opsi-status"
                      placeholder="mis. Garansi Toko"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <p className="text-[10px] text-slate-400 mt-1">
              Bawaan: {(bawaan.status_unit || []).join(", ") || "-"}
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Kelengkapan tambahan</label>
            <textarea value={kelengkapan} onChange={e => setKelengkapan(e.target.value)} rows={3}
                      data-testid="konfig-opsi-kelengkapan"
                      placeholder="mis. Minus Box"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <p className="text-[10px] text-slate-400 mt-1">
              Bawaan: {(bawaan.kelengkapan || []).join(", ") || "-"}
            </p>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 rounded-lg border border-slate-200 text-sm">Tutup</button>
          <button onClick={simpan} disabled={busy} data-testid="konfig-opsi-simpan"
                  className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
            Simpan
          </button>
        </div>
      </div>
    </div>
  );
}
